// Pure derivation of a CloudFormation stack's history from its events, for the
// StackSet instance Events panel (attempt selector + timeline view).
//
// Events newest-first or any order → attempts → bars.
// An attempt starts at each root-stack event (ResourceType
// 'AWS::CloudFormation::Stack', LogicalResourceId === stack name) whose status
// is CREATE_IN_PROGRESS | UPDATE_IN_PROGRESS | DELETE_IN_PROGRESS.
// For each logical id inside an attempt: start = first *_IN_PROGRESS,
// end = first later terminal (*_COMPLETE | *_FAILED | *_SKIPPED), result =
// 'success' | 'failed' | 'rollback' | 'in_progress' (no terminal yet → end = now).
//
// buildAttempts(events, stackName, now = Date.now()) →
//   [{ startedAt, endedAt, status, kind, finalStatus, cleanup, events,
//      bars: [{ logicalId, type, physicalId, start, end, from, to, result,
//               reason, rootCause, segments, caps, events }] }]
// Attempts are oldest-first (the latest is the last element); times are epoch ms;
// each attempt's `events` are its own events, newest-first (table order).
//
// Like the AWS console timeline, a bar is drawn as status segments rather than
// one colour: `segments` are the spans a resource spent in progress
// ({ kind: 'progress' | 'rollback', start, end, inferred }) and `caps` are the
// terminal statuses it reached ({ kind: 'complete' | 'failed' |
// 'rollback_complete', at, status }). A resource whose only event in the
// attempt is a *_COMPLETE (CloudFormation logs no *_IN_PROGRESS when it cleans
// up a resource that never got created) gets an `inferred` segment from the
// attempt start, so the time spent on it is not drawn as an empty gap.
// `rootCause` marks the attempt's first real failure (not a "creation
// cancelled" knock-on failure) — AWS's "Likely root cause".
//
// `cleanup` marks a DELETE attempt that is CloudFormation removing the stack a
// failed CREATE left behind (StackSets deletes it straight away, so a retry can
// create it afresh). It is read from the delete's own root event ("The
// following resource(s) failed to create: …"), or from the previous attempt
// being a failed CREATE.

export const ROOT_STACK_TYPE = 'AWS::CloudFormation::Stack';

const ATTEMPT_START = new Set(['CREATE_IN_PROGRESS', 'UPDATE_IN_PROGRESS', 'DELETE_IN_PROGRESS']);

const isInProgress = (status) => /_IN_PROGRESS$/.test(status || '');
const isTerminal = (status) => /(_COMPLETE|_FAILED|_SKIPPED)$/.test(status || '');
const isFailed = (status) => /_FAILED$/.test(status || '');

const ts = (ev) => {
  const t = new Date(ev.timestamp).getTime();
  return Number.isFinite(t) ? t : 0;
};

const isRoot = (ev, stackName) => (ev.resourceType === ROOT_STACK_TYPE)
  && ev.logicalResourceId === (stackName || ev.stackName);

// Root-stack final status → attempt status.
function attemptStatusFromRoot(status) {
  if (!status) return null;
  if (isInProgress(status)) return 'in_progress';
  if (/ROLLBACK_COMPLETE$/.test(status)) return 'rollback';
  if (isFailed(status)) return 'failed';
  if (/_COMPLETE$/.test(status)) return 'success';
  return null;
}

function kindOf(status) {
  if (/^CREATE/.test(status || '')) return 'create';
  if (/^UPDATE/.test(status || '')) return 'update';
  if (/^DELETE/.test(status || '')) return 'delete';
  return 'unknown';
}

// Rollback context: the attempt is a cleanup, the status itself is a rollback
// status, or the event came after the stack started rolling back.
const inRollback = (ev, cleanup, rollbackAt) => cleanup
  || /ROLLBACK/.test(ev.resourceStatus || '')
  || (rollbackAt !== null && ts(ev) >= rollbackAt);

// buildSegments turns one resource's chronological events into in-progress
// segments and terminal caps. An open segment ends at `openEnd`.
function buildSegments(evs, ctx) {
  const { cleanup, rollbackAt, attemptStart, openEnd } = ctx;
  const segments = [];
  const caps = [];
  let open = null;
  evs.forEach((e) => {
    const t = ts(e);
    const rb = inRollback(e, cleanup, rollbackAt);
    if (isInProgress(e.resourceStatus)) {
      const kind = rb ? 'rollback' : 'progress';
      if (open && open.kind !== kind) {
        segments.push({ ...open, end: t });
        open = null;
      }
      if (!open) open = { kind, start: t, inferred: false };
      return;
    }
    if (!isTerminal(e.resourceStatus)) return;
    if (open) {
      segments.push({ ...open, end: t });
      open = null;
    } else if (segments.length === 0 && caps.length === 0
      && /_COMPLETE$/.test(e.resourceStatus) && t > attemptStart) {
      segments.push({
        kind: rb ? 'rollback' : 'progress',
        start: rb && rollbackAt !== null && !cleanup ? rollbackAt : attemptStart,
        end: t,
        inferred: true,
      });
    }
    let kind = 'complete';
    if (isFailed(e.resourceStatus)) kind = 'failed';
    else if (rb) kind = 'rollback_complete';
    caps.push({ kind, at: t, status: e.resourceStatus });
  });
  if (open) segments.push({ ...open, end: Math.max(openEnd, open.start) });
  return { segments, caps };
}

function buildBars(attemptEvents, stackName, ctx) {
  const { rollbackAt, openEnd } = ctx;
  const byId = new Map();
  attemptEvents.forEach((ev) => {
    if (isRoot(ev, stackName)) return; // the attempt itself is the root's span
    const id = ev.logicalResourceId || '';
    if (!byId.has(id)) byId.set(id, []);
    byId.get(id).push(ev);
  });

  const bars = [];
  byId.forEach((evs, logicalId) => {
    const startIdx = evs.findIndex((e) => isInProgress(e.resourceStatus));
    const first = startIdx >= 0 ? evs[startIdx] : evs[0];
    const from = startIdx >= 0 ? startIdx + 1 : 0;
    let terminal = null;
    for (let i = from; i < evs.length; i += 1) {
      if (isTerminal(evs[i].resourceStatus)) { terminal = evs[i]; break; }
    }
    if (!terminal && startIdx < 0) terminal = evs.find((e) => isTerminal(e.resourceStatus)) || null;

    const start = ts(first);
    let result;
    let end;
    if (!terminal) {
      result = 'in_progress';
      end = Math.max(openEnd, start);
    } else {
      end = ts(terminal);
      if (isFailed(terminal.resourceStatus)) {
        result = 'failed';
      } else if (rollbackAt !== null && evs.some((e) => ts(e) >= rollbackAt)) {
        result = 'rollback';
      } else {
        result = 'success';
      }
    }
    const { segments, caps } = buildSegments(evs, ctx);
    // start/end are the first span (start → first terminal); from/to are the
    // full extent including any rollback / cleanup segments.
    const times = [start, end, ...segments.flatMap((g) => [g.start, g.end]), ...caps.map((c) => c.at)];
    const failedEv = evs.find((e) => isFailed(e.resourceStatus) && e.resourceStatusReason);
    const reason = (failedEv && failedEv.resourceStatusReason)
      || (terminal && terminal.resourceStatusReason) || '';
    const last = evs[evs.length - 1];
    bars.push({
      logicalId,
      type: first.resourceType || '',
      physicalId: (last && last.physicalResourceId) || first.physicalResourceId || '',
      start,
      end,
      result,
      reason,
      from: Math.min(...times),
      to: Math.max(...times),
      rootCause: false,
      segments,
      caps,
      events: evs,
    });
  });
  bars.sort((a, b) => (a.from - b.from) || a.logicalId.localeCompare(b.logicalId));

  // Likely root cause: the earliest failure that is not a knock-on
  // "Resource creation cancelled" (or update/delete cancelled) failure.
  let root = null;
  bars.forEach((b) => {
    const cap = b.caps.find((c) => c.kind === 'failed');
    if (!cap || /cancel/i.test(b.reason)) return;
    if (!root || cap.at < root.at) root = { bar: b, at: cap.at };
  });
  if (root) root.bar.rootCause = true;
  return bars;
}

// The root DELETE_IN_PROGRESS of a StackSets cleanup carries CloudFormation's
// own explanation: "The following resource(s) failed to create: [X]. …".
const CLEANUP_REASON = /failed to create/i;

export function buildAttempts(events, stackName, now = Date.now()) {
  if (!Array.isArray(events) || events.length === 0) return [];
  // Chronological. AWS returns newest-first, so for equal timestamps the later
  // input index is the earlier event.
  const ordered = events
    .map((ev, i) => ({ ev, i }))
    .sort((a, b) => (ts(a.ev) - ts(b.ev)) || (b.i - a.i))
    .map((x) => x.ev);
  const name = stackName || (ordered.find((e) => e.stackName) || {}).stackName || '';

  // Split into attempts at each root *_IN_PROGRESS start event. Events before
  // the first start (a truncated history) form a leading attempt of their own.
  // A repeated root *_IN_PROGRESS of the same kind before the attempt has
  // finished is not a new attempt: CloudFormation logs one mid-operation with
  // "Eventual consistency check initiated".
  const groups = [];
  let current = null;
  let currentKind = null;
  let currentDone = false;
  ordered.forEach((ev) => {
    const root = isRoot(ev, name);
    if (root && ATTEMPT_START.has(ev.resourceStatus)) {
      const kind = kindOf(ev.resourceStatus);
      if (!current || currentDone || kind !== currentKind) {
        current = [ev];
        groups.push(current);
        currentKind = kind;
        currentDone = false;
        return;
      }
    }
    if (!current) {
      current = [];
      groups.push(current);
    }
    current.push(ev);
    if (root && isTerminal(ev.resourceStatus)) currentDone = true;
  });

  // Each attempt's open spans end where the next attempt starts (a superseded
  // attempt does not keep growing); the latest attempt's end at `now`.
  const starts = groups.map((evs) => (evs.length ? ts(evs[0]) : now));
  const out = [];
  groups.forEach((evs, gi) => {
    const rootEvents = evs.filter((e) => isRoot(e, name));
    const lastRoot = rootEvents[rootEvents.length - 1];
    const startRoot = rootEvents.find((e) => ATTEMPT_START.has(e.resourceStatus));
    const kind = kindOf(startRoot ? startRoot.resourceStatus : (lastRoot && lastRoot.resourceStatus));
    const prev = out[gi - 1];
    const cleanup = kind === 'delete' && !!prev && prev.kind === 'create'
      && ((startRoot && CLEANUP_REASON.test(startRoot.resourceStatusReason || ''))
        || prev.status === 'failed' || prev.status === 'rollback');
    const rollbackEv = rootEvents.find((e) => /ROLLBACK_IN_PROGRESS$/.test(e.resourceStatus));
    const rollbackAt = rollbackEv ? ts(rollbackEv) : null;
    const superseded = gi < groups.length - 1;
    const openEnd = superseded ? starts[gi + 1] : now;
    const startedAt = ts(evs[0]);
    const bars = buildBars(evs, name, {
      cleanup, rollbackAt, attemptStart: startedAt, openEnd,
    });

    let status = attemptStatusFromRoot(lastRoot && lastRoot.resourceStatus);
    if (!status) {
      if (bars.some((b) => b.result === 'failed')) status = 'failed';
      else if (bars.some((b) => b.result === 'in_progress')) status = 'in_progress';
      else status = 'success';
    }
    let endedAt;
    if (status === 'in_progress') {
      endedAt = Math.max(openEnd, startedAt);
    } else {
      endedAt = Math.max(ts(evs[evs.length - 1]), ...bars.map((b) => b.end));
    }
    out.push({
      startedAt,
      endedAt,
      status,
      kind,
      cleanup,
      finalStatus: lastRoot ? lastRoot.resourceStatus : '',
      events: evs.slice().reverse(),
      bars,
    });
  });
  return out;
}

// withCleanup returns the attempt to draw on the timeline for attempts[i]: a
// CREATE followed by its automatic cleanup DELETE is drawn as one timeline (the
// way the AWS console draws a failure and its rollback on the same rows), with
// the cleanup's spans in the rollback colours. Any other attempt is returned
// as is. The merged attempt has `cleanupStatus` (the cleanup's own status).
export function withCleanup(attempts, i) {
  const a = attempts[i];
  const b = attempts[i + 1];
  if (!a || !b || !b.cleanup || a.kind !== 'create') return a || null;
  const byId = new Map(a.bars.map((bar) => [bar.logicalId, { ...bar }]));
  b.bars.forEach((bar) => {
    const cur = byId.get(bar.logicalId);
    if (!cur) {
      byId.set(bar.logicalId, { ...bar, rootCause: false });
      return;
    }
    byId.set(bar.logicalId, {
      ...cur,
      from: Math.min(cur.from, bar.from),
      to: Math.max(cur.to, bar.to),
      reason: cur.reason || bar.reason,
      physicalId: cur.physicalId || bar.physicalId,
      segments: [...cur.segments, ...bar.segments],
      caps: [...cur.caps, ...bar.caps],
      events: [...cur.events, ...bar.events],
    });
  });
  const bars = Array.from(byId.values())
    .sort((x, y) => (x.from - y.from) || x.logicalId.localeCompare(y.logicalId));
  return {
    ...a,
    endedAt: Math.max(b.endedAt, ...bars.map((bar) => bar.to)),
    cleanupStatus: b.status,
    cleanupFinalStatus: b.finalStatus,
    bars,
  };
}

// Tick steps for the time axis, smallest first.
const TICK_STEPS_MS = [1, 2, 5, 10, 15, 30, 60, 120, 300, 600, 900, 1800, 3600, 7200, 21600, 43200, 86400]
  .map((s) => s * 1000);

// timeTicks returns evenly spaced tick times on round boundaries inside
// [start, end], at most maxTicks of them (the AWS timeline's 5s-style grid).
export function timeTicks(start, end, maxTicks = 8) {
  const span = Math.max(end - start, 0);
  if (!Number.isFinite(span) || span === 0) return [start];
  const step = TICK_STEPS_MS.find((s) => span / s <= maxTicks) || TICK_STEPS_MS[TICK_STEPS_MS.length - 1];
  const ticks = [];
  for (let t = Math.ceil(start / step) * step; t <= end; t += step) ticks.push(t);
  return ticks;
}

// parseFailureReason splits a StackSets failure reason into a readable summary
// and the raw text. AWS reports a failed stack instance as
//   ResourceLogicalId:X, ResourceType:T, ResourceStatusReason:Resource handler
//   returned message: "msg (Service: …, Request ID: …) (SDK Attempt Count: 1)"
//   (RequestToken: …, HandlerErrorCode: …).
// → { summary: "X (T): msg", details: <raw> }. Anything else is returned as
// { summary: raw, details: null }.
export function parseFailureReason(raw) {
  const text = String(raw || '').trim();
  if (!text) return { summary: '', details: null };
  const m = /^ResourceLogicalId:\s*([^,]+),\s*ResourceType:\s*([^,]+),\s*ResourceStatusReason:\s*([\s\S]*)$/.exec(text);
  if (!m) return { summary: text, details: null };
  const [, logicalId, type, inner] = m;
  let msg = inner.trim();
  const handler = /^Resource handler returned message:\s*"([\s\S]*)"\s*(\(RequestToken[\s\S]*)?$/.exec(msg);
  if (handler) msg = handler[1];
  msg = msg
    .replace(/\s*\((?:Service|SDK Attempt Count|RequestToken):[^)]*\)/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\.$/, '');
  return { summary: `${logicalId.trim()} (${type.trim()}): ${msg}`, details: text };
}

// formatDuration renders milliseconds as "1h 2m", "3m 4s" or "5s".
export function formatDuration(ms) {
  const total = Math.max(0, Math.round((ms || 0) / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

// isRoleTrustReason reports whether a reason is the execution role not being
// usable (the issue-3 case): CloudFormation's trust-relationship message, or
// Prism's own "execution role … not ready / cannot assume" reasons.
export function isRoleTrustReason(reason) {
  const r = String(reason || '');
  return /trust relationship/i.test(r) || /execution role/i.test(r);
}
